/**
 * MobileActionBarView - Plus pill (+) button and picker overlay
 *
 * The plus button opens a picker with "Add a report", "Add to list",
 * and for manual players, "Edit player", "Delete player",
 * and "Transfer reports & notes" (admin only, disabled when 0 reports and 0 notes).
 * On mobile: delegates to MobilePickerOverlay (full-viewport Actionsheet).
 * On desktop: delegates to a Popover (portal-based) wrapping DesktopPickerOverlay.
 * Navigation back is handled by the breadcrumb — no close button needed.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import { ChevronDown, Plus } from "lucide-react-native";
import { memo } from "react";
import { Pressable } from "@/components/atoms/Pressable";

import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Popover,
  PopoverBackdrop,
  PopoverContent,
} from "@/components/atoms/Popover";

import DesktopPickerOverlay from "@/features/player-detail/components/molecules/DesktopPickerOverlay";
import MobilePickerOverlay from "@/features/player-detail/components/molecules/MobilePickerOverlay";

/**
 * Get pill background class for the plus button
 */
const getAddPillClass = (): string =>
  "flex-row items-center rounded-full bg-surface-card/80 py-1 pl-3 pr-2";

/**
 * Get icon class for action bar buttons
 */
const getActionBarIconClass = (): string => "text-content-primary";

/**
 * Get chevron class for the dropdown indicator
 */
const getActionBarChevronClass = (): string => "text-content-muted";

/**
 * Props for MobileActionBarView
 */
export interface MobileActionBarViewProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether to show the plus button */
  readonly showPlusButton: boolean;
  /** Callback when plus button is pressed (opens mobile picker) */
  readonly onPlusPress: () => void;
  /** Whether the picker is open */
  readonly isPickerOpen: boolean;
  /** Callback to close the picker */
  readonly onPickerClose: () => void;
  /** Callback for "Add a report" option (hidden if undefined) */
  readonly onAddReport?: () => void;
  /** Callback for "Add to list" option (hidden if undefined) */
  readonly onAddToList?: () => void;
  /** Callback for "Edit player" option — manual players only (hidden if undefined) */
  readonly onEditPlayer?: () => void;
  /** Callback for "Delete player" option — manual players only (hidden if undefined) */
  readonly onDeletePlayer?: () => void;
  /** Callback for "Transfer reports & notes" — admin users on manual players only */
  readonly onTransferReports?: () => void;
  /** Whether the transfer reports action is disabled (0 reports and 0 notes) */
  readonly isTransferDisabled?: boolean;
  /** Whether the screen is mobile sized — controls picker overlay type */
  readonly isSmallScreen?: boolean;
  /** Render function for the Popover trigger (+ button) on desktop */
  readonly renderPickerTrigger: (
    triggerProps: Record<string, unknown>
  ) => React.JSX.Element;
}

/**
 * MobileActionBarView renders the plus pill, close button, and picker overlay
 * @param props - View props for action bar display
 * @param props.isDark - Whether dark mode is active
 * @param props.showPlusButton - Whether to show the plus button
 * @param props.onPlusPress - Callback when plus button is pressed (mobile only)
 * @param props.isPickerOpen - Whether the picker is open
 * @param props.onPickerClose - Callback to close the picker
 * @param props.onAddReport - Callback for "Add a report"
 * @param props.onAddToList - Callback for "Add to list"
 * @param props.onEditPlayer - Callback for "Edit player" (manual players only)
 * @param props.onDeletePlayer - Callback for "Delete player" (manual players only)
 * @param props.onTransferReports - Callback for "Transfer reports & notes" (admin on manual players)
 * @param props.isTransferDisabled - Whether the transfer action is disabled (0 reports and 0 notes)
 * @param props.isSmallScreen - Whether mobile sized (Actionsheet vs Popover)
 * @param props.renderPickerTrigger - Render function for desktop Popover trigger
 * @returns HStack with pill-shaped plus button and picker overlay
 */
const MobileActionBarView = ({
  isDark,
  showPlusButton,
  onPlusPress,
  isPickerOpen,
  onPickerClose,
  onAddReport,
  onAddToList,
  onEditPlayer,
  onDeletePlayer,
  onTransferReports,
  isTransferDisabled,
  isSmallScreen = false,
  renderPickerTrigger,
}: MobileActionBarViewProps) => (
  <>
    <Stack direction="horizontal" className="items-center justify-end gap-4">
      {showPlusButton ? (
        isSmallScreen ? (
          <Pressable
            onPress={onPlusPress}
            className={getAddPillClass()}
            accessibilityRole="button"
            accessibilityLabel="Add action"
          >
            <Icon as={Plus} size="sm" className={getActionBarIconClass()} />
            <Icon
              as={ChevronDown}
              size="2xs"
              className={getActionBarChevronClass()}
            />
          </Pressable>
        ) : (
          <Popover
            isOpen={isPickerOpen}
            onClose={onPickerClose}
            onOpen={onPlusPress}
            placement="bottom right"
            offset={4}
            size="xs"
            trigger={renderPickerTrigger}
          >
            <PopoverBackdrop className="bg-transparent" />
            <PopoverContent className="border-0 bg-transparent p-0 shadow-none">
              <DesktopPickerOverlay
                isDark={isDark}
                onAddReport={onAddReport}
                onAddToList={onAddToList}
                onEditPlayer={onEditPlayer}
                onDeletePlayer={onDeletePlayer}
                onTransferReports={onTransferReports}
                isTransferDisabled={isTransferDisabled}
              />
            </PopoverContent>
          </Popover>
        )
      ) : null}
    </Stack>

    {/* Mobile: canonical MobileActionMenu sheet (SE-5546) */}
    {isSmallScreen ? (
      <MobilePickerOverlay
        isPickerOpen={isPickerOpen}
        onPickerClose={onPickerClose}
        onAddReport={onAddReport}
        onAddToList={onAddToList}
        onEditPlayer={onEditPlayer}
        onDeletePlayer={onDeletePlayer}
        onTransferReports={onTransferReports}
        isTransferDisabled={isTransferDisabled}
      />
    ) : null}
  </>
);

MobileActionBarView.displayName = "MobileActionBarView";

export default memo(MobileActionBarView);
